import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { contactAffiche } from '../../api/contactCompte'
import {
  envoyerMessage,
  listerClients,
  listerDiscussionsClientLivreur,
  listerFils,
  listerLivreurs,
  listerMarchands,
  marquerFilLu,
  suivreMessages,
} from '../../api/dispatch'
import type { Client, FilsParPair, Livreur, Marchand } from '../../api/types'
import {
  LIBELLE_CASQUETTE,
  cleFil,
  lireCleFil,
  titreDiscussion,
  type Casquette,
  type DiscussionCommande,
} from '../../api/discussions'

/**
 * Messagerie du Dispatch — structure et classes REPRISES de l'originale.
 *
 * `.chat-wrap` / `.chat-list` / `.chat-main` / `.chat-head` / `.chat-msgs` /
 * `.chat-input` portent toute la disposition ; `.chat-item` + `.ci-*` la liste
 * des interlocuteurs ; `.dch*` les bulles, avec leur regroupement par auteur
 * et par jour. Les remplacer par des `<div>` à styles en ligne produit un
 * écran qui n'a plus rien à voir — c'est ce qui s'était passé.
 */
type Categorie = 'drivers' | 'merchants' | 'clients' | 'discussions'

/**
 * `discussions` n'est PAS une conversation avec le dispatch : ce sont les
 * échanges entre un client et SON livreur, que le dispatch lit pour arbitrer un
 * litige. Lecture seule, un fil par commande.
 */
const CATEGORIES: Record<Categorie, string> = {
  drivers: 'Livreurs',
  merchants: 'Marchands',
  clients: 'Clients',
  discussions: 'Client \u2194 Livreur',
}

/**
 * Chaque catégorie d'assistance est UNE casquette (migration 0053) : la même
 * personne peut figurer chez les Livreurs ET chez les Clients, avec deux fils
 * distincts — la réponse part dans l'app correspondante.
 */
const CASQUETTE_DE: Record<Exclude<Categorie, 'discussions'>, Casquette> = {
  drivers: 'livreur',
  merchants: 'marchand',
  clients: 'client',
}
const CATEGORIE_DE: Record<Casquette, Exclude<Categorie, 'discussions'>> = {
  livreur: 'drivers',
  marchand: 'merchants',
  client: 'clients',
}

/** L'étiquette visible d'un fil : « Client », « Livreur » ou « Marchand ». */
function Etiquette({ casquette }: { casquette: Casquette }) {
  const teinte = casquette === 'livreur' ? 'badge-blue' : casquette === 'marchand' ? 'badge-yellow' : 'badge-green'
  return (
    <span className={`badge ${teinte}`} style={{ marginLeft: 8, verticalAlign: 'middle' }}>
      {LIBELLE_CASQUETTE[casquette]}
    </span>
  )
}

const REGROUPEMENT_MS = 5 * 60_000

function proches(
  a: { de: string; envoyeLe: string } | undefined,
  b: { de: string; envoyeLe: string } | undefined,
): boolean {
  if (!a || !b) return false
  if (a.de !== b.de || a.de === 'system') return false
  const ecart = Math.abs(new Date(b.envoyeLe).getTime() - new Date(a.envoyeLe).getTime())
  return ecart < REGROUPEMENT_MS && a.envoyeLe.slice(0, 10) === b.envoyeLe.slice(0, 10)
}

function libelleJour(iso: string): string {
  const d = iso.slice(0, 10)
  if (d === new Date().toISOString().slice(0, 10)) return "Aujourd'hui"
  if (d === new Date(Date.now() - 864e5).toISOString().slice(0, 10)) return 'Hier'
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
}

const initiales = (nom: string) =>
  nom.split(/\s+/).filter(Boolean).slice(0, 2).map((m) => m[0]?.toUpperCase() ?? '').join('') || '?'

interface Pair {
  /** `cleFil(personne, casquette)` — ou l'id de commande pour un fil client ↔ livreur. */
  id: string
  nom: string
  sous: string
  /** État du livreur, pour la pastille. Absent pour clients et marchands. */
  etat?: 'online' | 'busy' | 'offline'
  /** Remplace les initiales du nom (une commande n'a pas d'initiales : « C# »). */
  avatar?: string
  /** Pas de pastille d'état : un fil de commande n'est pas « en ligne ». */
  sansPastille?: boolean
}

export function EcranChat({
  pairInitial,
  discussionInitiale,
}: {
  pairInitial?: string | null
  /** Commande dont on ouvre directement le fil client ↔ livreur (depuis sa fiche). */
  discussionInitiale?: string | null
}) {
  const [fils, setFils] = useState<FilsParPair>({})
  const [discussions, setDiscussions] = useState<DiscussionCommande[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [livreurs, setLivreurs] = useState<Livreur[]>([])
  const [marchands, setMarchands] = useState<Marchand[]>([])
  const casquetteInitiale = pairInitial ? lireCleFil(pairInitial)?.casquette : undefined
  const [categorie, setCategorie] = useState<Categorie>(
    discussionInitiale ? 'discussions' : casquetteInitiale ? CATEGORIE_DE[casquetteInitiale] : 'drivers',
  )
  const [pairActif, setPairActif] = useState<string | null>(discussionInitiale ?? pairInitial ?? null)
  const [selecteurOuvert, setSelecteurOuvert] = useState(false)
  const [brouillon, setBrouillon] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const bas = useRef<HTMLDivElement>(null)

  async function chargerFils() {
    try {
      const [normaux, croises] = await Promise.all([listerFils(), listerDiscussionsClientLivreur()])
      setFils(normaux)
      setDiscussions(croises)
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Messagerie indisponible.')
    }
  }

  useEffect(() => {
    void chargerFils()
    void Promise.all([listerClients(), listerLivreurs(), listerMarchands()])
      .then(([c, l, m]) => {
        setClients(c)
        setLivreurs(l)
        setMarchands(m)
      })
      .catch(() => {
        /* Les noms manquants dégradent l'affichage, pas la messagerie. */
      })
    return suivreMessages(() => void chargerFils())
  }, [])

  useEffect(() => {
    bas.current?.scrollIntoView({ behavior: 'smooth' })
  }, [fils, discussions, pairActif])

  // Un clic n'importe où ailleurs referme le sélecteur — le composant arrête
  // la propagation sur lui-même pour ne pas se refermer au premier clic.
  useEffect(() => {
    if (!selecteurOuvert) return
    const fermer = () => setSelecteurOuvert(false)
    document.addEventListener('click', fermer)
    return () => document.removeEventListener('click', fermer)
  }, [selecteurOuvert])

  const pairs: Pair[] = useMemo(() => {
    if (categorie === 'discussions') {
      return discussions.map((d) => ({
        id: d.orderId,
        nom: `#${d.ref ?? d.orderId.slice(0, 6).toUpperCase()}`,
        avatar: '\u2194',
        sansPastille: true,
        sous: `${d.clientNom} \u2194 ${d.livreurNom} \u00b7 ${d.messages.length} message${d.messages.length > 1 ? 's' : ''}`,
      }))
    }
    const casquette = CASQUETTE_DE[categorie]
    let base: Pair[]
    if (categorie === 'drivers') {
      base = livreurs.map((l) => ({
        id: cleFil(l.id, casquette),
        nom: l.nom,
        sous: l.telephone || l.modele,
        etat: l.etat === 'offline' ? 'offline' : l.etat === 'busy' ? 'busy' : 'online',
      }))
    } else if (categorie === 'merchants') {
      // Le pair d'un marchand est le profil du GÉRANT, pas l'établissement :
      // sans compte, il n'y a personne à qui écrire.
      base = marchands
        .filter((m) => m.proprietaireId)
        .map((m) => ({
          id: cleFil(m.proprietaireId as string, casquette),
          nom: m.nom,
          sous: m.nomContact || contactAffiche(m.email, m.telephoneGerant),
        }))
    } else {
      base = clients.map((c) => ({ id: cleFil(c.id, casquette), nom: c.nom, sous: c.telephone || c.email }))
    }
    /* Un fil de cette casquette dont la personne n'est pas dans l'annuaire (un
       équipier marchand qui n'est pas le gérant, par exemple) : on le montre
       quand même — un message reçu ne doit jamais rester invisible. */
    const connus = new Set(base.map((p) => p.id))
    const horsAnnuaire = Object.keys(fils)
      .filter((cle) => !connus.has(cle) && lireCleFil(cle)?.casquette === casquette)
      .map((cle) => ({ id: cle, nom: 'Compte hors annuaire', sous: lireCleFil(cle)?.personne.slice(0, 8) ?? '' }))
    return [...horsAnnuaire, ...base]
  }, [categorie, livreurs, marchands, clients, discussions, fils])

  const nonLus = (id: string) =>
    (fils[id] ?? []).filter((m) => m.de === 'them' && !m.lu).length

  /** Les interlocuteurs d'une catégorie donnée — pas seulement de la courante. */
  const pairsDe = (c: Categorie): string[] => {
    if (c === 'discussions') return discussions.map((d) => d.orderId)
    const casquette = CASQUETTE_DE[c]
    const annuaire =
      c === 'drivers'
        ? livreurs.map((l) => l.id)
        : c === 'merchants'
          ? marchands.map((m) => m.proprietaireId).filter((x): x is string => Boolean(x))
          : clients.map((x) => x.id)
    const cles = new Set(annuaire.map((id) => cleFil(id, casquette)))
    for (const cle of Object.keys(fils)) if (lireCleFil(cle)?.casquette === casquette) cles.add(cle)
    return [...cles]
  }

  const nbPairs = (c: Categorie) => pairsDe(c).length

  /**
   * Non-lus des AUTRES catégories, agrégés dans la pastille du sélecteur.
   *
   * Sans ce compteur, un message d'un marchand reste invisible tant qu'on
   * regarde les livreurs : il faut ouvrir chaque catégorie pour savoir s'il
   * s'y passe quelque chose.
   */
  const nonLusAutresCategories = (Object.keys(CATEGORIES) as Categorie[])
    .filter((c) => c !== categorie)
    .reduce((s, c) => s + pairsDe(c).reduce((n, id) => n + nonLus(id), 0), 0)

  async function ouvrir(id: string) {
    setPairActif(id)
    // Un fil client ↔ livreur ne s'adresse pas au dispatch : l'ouvrir ne doit
    // rien marquer comme lu (ce sont les messages du livreur ou du client).
    if (categorie === 'discussions') return
    try {
      await marquerFilLu(id)
      await chargerFils()
    } catch {
      /* Non bloquant : ne pas marquer comme lu n'empêche pas de lire. */
    }
  }

  async function envoyer(e: FormEvent) {
    e.preventDefault()
    const texte = brouillon.trim()
    if (!texte || !pairActif) return
    setBrouillon('')
    try {
      await envoyerMessage(pairActif, texte)
      await chargerFils()
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Envoi impossible.')
      setBrouillon(texte)
    }
  }

  const lectureSeule = categorie === 'discussions'
  const discussionActive = lectureSeule ? discussions.find((d) => d.orderId === pairActif) : undefined
  const messages = pairActif && !lectureSeule ? fils[pairActif] ?? [] : []
  const nomActif = pairs.find((p) => p.id === pairActif)?.nom
  const casquetteActive = !lectureSeule && pairActif ? lireCleFil(pairActif)?.casquette : undefined

  const vide =
    categorie === 'drivers'
      ? 'Aucun livreur.'
      : categorie === 'merchants'
        ? 'Aucun marchand avec un compte.'
        : categorie === 'discussions'
          ? "Aucun échange client \u2194 livreur pour l'instant."
          : 'Aucun client (un compte apparaît ici après sa 1re commande ou son 1er message en tant que client).'

  return (
    <div className="chat-wrap">
      <div className="chat-list">
        {/* ⚠️ Un menu DÉROULANT, pas trois boutons côte à côte. Le CSS le
            construit ainsi : `.cp-menu` est replié (opacité 0, décalé) et ne
            s'ouvre qu'avec `.open` sur `.chat-pick`. Affiché à plat, il
            empilait ses trois options par-dessus la liste des conversations.
            La pastille `.cp-other` compte les non-lus des AUTRES catégories :
            sans elle, un message d'un marchand reste invisible tant qu'on
            regarde les livreurs. */}
        <div
          className={`chat-pick${selecteurOuvert ? ' open' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="cp-head"
            aria-haspopup="listbox"
            onClick={() => setSelecteurOuvert((o) => !o)}
          >
            <span className="cp-cur">{CATEGORIES[categorie]}</span>
            <span className="cp-n">{nbPairs(categorie)}</span>
            {nonLusAutresCategories > 0 && (
              <span
                className="chat-bubble cp-other"
                title="Non lus dans les autres catégories"
              >
                {nonLusAutresCategories > 99 ? '99+' : nonLusAutresCategories}
              </span>
            )}
            <svg
              className="cp-chev"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <div className="cp-menu" role="listbox">
            {(Object.keys(CATEGORIES) as Categorie[]).map((c) => (
              <button
                key={c}
                role="option"
                aria-selected={c === categorie}
                className={`cp-opt${c === categorie ? ' on' : ''}`}
                onClick={() => {
                  setCategorie(c)
                  setPairActif(null)
                  setSelecteurOuvert(false)
                }}
              >
                <span>{CATEGORIES[c]}</span>
                <span className="cp-n">{nbPairs(c)}</span>
              </button>
            ))}
          </div>
        </div>

        {pairs.length === 0 && (
          <div style={{ padding: 14, color: 'var(--n400)', fontSize: 12 }}>{vide}</div>
        )}

        {pairs.map((p) => {
          const n = nonLus(p.id)
          return (
            <div
              key={p.id}
              className={`chat-item${pairActif === p.id ? ' active' : ''}`}
              onClick={() => void ouvrir(p.id)}
            >
              {/* La pastille d'ÉTAT : sur la liste des livreurs, elle dit qui
                  est joignable avant même d'ouvrir la conversation. */}
              {!p.sansPastille && <span className={`dot ${p.etat ?? 'online'}`} />}
              <span className="ci-av">{p.avatar ?? initiales(p.nom)}</span>
              <div className="ci-txt ci-b">
                <div className="ci-n1 ci-n">
                  {p.nom}
                  {categorie !== 'discussions' && lireCleFil(p.id) && (
                    <Etiquette casquette={lireCleFil(p.id)!.casquette} />
                  )}
                </div>
                {p.sous && <div className="ci-n2 ci-s">{p.sous}</div>}
              </div>
              {n > 0 && <span className="chat-bubble">{n}</span>}
            </div>
          )
        })}
      </div>

      <div className="chat-main">
        <div className="chat-head">
          {discussionActive
            ? titreDiscussion(discussionActive)
            : lectureSeule
              ? 'Sélectionnez une commande'
              : nomActif
                ? (
                    <>
                      {nomActif}
                      {casquetteActive && <Etiquette casquette={casquetteActive} />}
                    </>
                  )
                : 'Sélectionnez un interlocuteur'}
        </div>

        <div className="chat-msgs">
          {discussionActive && <FilClientLivreur discussion={discussionActive} />}
          {messages.map((m, i) => {
            const precedent = messages[i - 1]
            const suivant = messages[i + 1]
            const premier = !proches(precedent, m)
            const dernier = !proches(m, suivant)
            const deMoi = m.de === 'dispatch'
            const nouveauJour = !precedent || precedent.envoyeLe.slice(0, 10) !== m.envoyeLe.slice(0, 10)

            if (m.de === 'system') {
              return (
                <div key={m.id}>
                  {nouveauJour && <div className="dch-day">{libelleJour(m.envoyeLe)}</div>}
                  <div className="dch-sys">{m.texte}</div>
                </div>
              )
            }

            return (
              <div key={m.id}>
                {nouveauJour && <div className="dch-day">{libelleJour(m.envoyeLe)}</div>}
                <div className={`dch ${deMoi ? 'me' : 'them'}${premier ? ' first' : ''}${dernier ? ' last' : ''}`}>
                  {/* `.ghost` garde la place de l'avatar sur les messages
                      groupés : sans lui, les bulles se décalent en escalier. */}
                  {!deMoi && (
                    <div className={`dch-av${dernier ? '' : ' ghost'}`}>
                      {initiales(nomActif ?? '?')}
                    </div>
                  )}
                  <div className="dch-w">
                    {!deMoi && premier && <div className="dch-sender">{nomActif}</div>}
                    {/* React échappe : ce texte vient d'un compte tiers. */}
                    <div className="dch-b">{m.texte}</div>
                    {dernier && (
                      <div className="dch-meta">
                        {m.envoyeLe.slice(11, 16)}
                        {deMoi && (
                          /* Tracés SVG, PAS « ✓✓ » : le double glyphe n'existe
                             pas dans toutes les polices, et seule la version
                             SVG passe au vert via `.dch-tick.read` — le signe
                             que le message a été LU, pas seulement reçu. */
                          <span className={`dch-tick${m.lu ? ' read' : ''}`}>
                            {m.lu ? (
                              <svg viewBox="0 0 24 24">
                                <polyline points="1 12.5 6 17.5 17 6.5" />
                                <polyline points="7.5 12.5 12 17.5 23 6.5" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24">
                                <polyline points="4 12.5 9 17.5 20 6.5" />
                              </svg>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
          <div ref={bas} />
        </div>

        {erreur && (
          <p style={{ color: 'var(--danger)', fontSize: 12, margin: '0 14px' }}>{erreur}</p>
        )}

        {lectureSeule ? (
          /* Pas de zone de saisie : le dispatch LIT ces échanges pour arbitrer
             un litige, il n'y écrit pas. Une zone grisée laisserait croire à
             une fonction qui arrive ; l'écrire en toutes lettres lève le doute. */
          <p className="chat-readonly">
            Lecture seule — échanges entre le client et son livreur, visibles pour arbitrer un litige.
          </p>
        ) : (
        <form className="chat-input" onSubmit={envoyer}>
          <textarea
            rows={1}
            maxLength={800}
            placeholder="Message…"
            disabled={!pairActif}
            value={brouillon}
            onChange={(e) => setBrouillon(e.target.value)}
          />
          <button type="submit" aria-label="Envoyer" title="Envoyer" disabled={!pairActif || !brouillon.trim()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </form>
        )}
      </div>
    </div>
  )
}

/**
 * Un fil client ↔ livreur : le client à GAUCHE, le livreur à DROITE, chacun
 * nommé en tête de groupe, avec l'heure. Aucune coche « lu » : ces messages ne
 * sont pas adressés au dispatch, il n'a rien à marquer.
 */
function FilClientLivreur({ discussion }: { discussion: DiscussionCommande }) {
  const { messages } = discussion
  return (
    <>
      {messages.map((m, i) => {
        const precedent = messages[i - 1]
        const suivant = messages[i + 1]
        const premier = !proches(precedent, m) || precedent?.nom !== m.nom
        const dernier = !proches(m, suivant) || suivant?.nom !== m.nom
        const nouveauJour = !precedent || precedent.envoyeLe.slice(0, 10) !== m.envoyeLe.slice(0, 10)
        const livreur = m.de === 'livreur'
        return (
          <div key={m.id}>
            {nouveauJour && <div className="dch-day">{libelleJour(m.envoyeLe)}</div>}
            <div className={`dch ${livreur ? 'me' : 'them'}${premier ? ' first' : ''}${dernier ? ' last' : ''}`}>
              {!livreur && <div className={`dch-av${dernier ? '' : ' ghost'}`}>{initiales(m.nom)}</div>}
              <div className="dch-w">
                {premier && (
                  <div className="dch-sender">
                    {livreur ? 'Livreur' : 'Client'} · {m.nom}
                  </div>
                )}
                {/* React échappe : ce texte vient de comptes tiers. */}
                <div className="dch-b">{m.texte}</div>
                {dernier && <div className="dch-meta">{m.envoyeLe.slice(11, 16)}</div>}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
